<template>
  <div>
    <div v-if="detail.is_volume_work">
      <h6 class="mt-5 font-weight-bold">Work Volume</h6>
      <b-button
        v-if="canAdd && detail.approval_status.isEdit && calculate.length !== 0"
        class="btn-custom mx-2 mb-2"
        variant="info"
        size="sm"
        @click="addWorkVolume"
      >
        Add Work Volume
      </b-button>
      <b-alert
        :show="(detail.status === 'Draft' || detail.status === 'Revise') && calculate.length === 0"
        variant="danger"
      >
        Complete contractor volume work data first to count Volume Price &amp; Total Price
      </b-alert>
      <b-table
        class="mt-2"
        bordered
        table-class="table-work-volume"
        thead-class="bg-color text-center"
        empty-text="No data"
        :items="detail.volume"
        :fields="fields"
        responsive
        show-empty
      >
        <template #thead-top>
          <b-tr>
            <b-th></b-th>
            <b-th></b-th>
            <b-th></b-th>
            <b-th></b-th>
            <b-th></b-th>
            <b-th></b-th>
            <b-th></b-th>
            <b-th></b-th>
            <b-th></b-th>
            <b-th colspan="5">Budget Number</b-th>
          </b-tr>
        </template>
        <template v-slot:cell(no)="items">
          {{ items.index + 1 }}
        </template>
        <template v-slot:cell(type_work_source)="items">
          {{ !detail.approval_status.isEdit ? items.item.type_work_source : '' }}
          <validation-provider
            v-if="detail.approval_status.isEdit || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required' : ''"
            v-slot="{ errors }"
            slim
          >
            <b-form-input
              autocapitalize="off"
              v-model="items.item.type_work_source"
              :class="{ 'is-invalid': errors.length }"
            />
          </validation-provider>
        </template>
        <template v-slot:cell(type_work_purpose)="items">
          {{ !detail.approval_status.isEdit ? items.item.type_work_purpose : '' }}
          <validation-provider
            v-if="detail.approval_status.isEdit || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required' : ''"
            v-slot="{ errors }"
            slim
          >
            <b-form-input
              autocapitalize="off"
              v-model="items.item.type_work_purpose"
              :class="{ 'is-invalid': errors.length }"
            />
          </validation-provider>
        </template>
        <template v-slot:cell(actual_distance)="items">
          {{ !detail.approval_status.isEdit ? items.item.actual_distance : '' }}
          <validation-provider
            v-if="detail.approval_status.isEdit || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required|min_value:1' : 'min_value:1'"
            v-slot="{ errors }"
            slim
          >
            <b-form-input
              autocapitalize="off"
              v-model="items.item.actual_distance"
              type="number"
              min="1"
              :class="{ 'is-invalid': errors.length }"
            />
          </validation-provider>
        </template>
        <template v-slot:cell(payment_distance)="items">
          {{ !detail.approval_status.isEdit ? formatPrice(items.item.payment_distance, 0) : '' }}
          {{
            detail.approval_status.isEdit || (canAdd && detail.status === 'Draft') ?
              formatPrice(getPaymentDistance(items.index, items.item.actual_distance), 0) : ''
          }}
        </template>
        <template v-slot:cell(volume_bcm)="items">
          {{ !detail.approval_status.isEdit ? formatPrice(items.item.volume_bcm) : '' }}
          <validation-provider
            v-if="detail.approval_status.isEdit || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required|min_value:1' : 'min_value:1'"
            v-slot="{ errors }"
            slim
          >
            <b-form-input
              autocapitalize="off"
              v-model="items.item.volume_bcm"
              type="number"
              min="1"
              :class="{ 'is-invalid': errors.length }"
            />
          </validation-provider>
        </template>
        <template v-slot:cell(volume_price)="items">
          {{ formatPrice(calVolumePrice(items.item)) }}
        </template>
        <template v-slot:cell(total_price)="items">
          {{ formatPrice(calTotalPrice(items.item)) }}
        </template>
        <template v-slot:cell(cc)="items">
          <validation-provider
            v-if="detail.approval_status.isEdit  || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required' : ''"
            v-slot="{ errors }"
            slim
          >
            <b-form-select
              v-model="items.item.cc"
              :options="ccList"
              size="sm"
              @input="onFieldChange('cc', items.item, items.index)"
              :class="{ 'is-invalid': errors.length }"
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  -
                </b-form-select-option>
              </template>
            </b-form-select>
          </validation-provider>
          {{ !detail.approval_status.isEdit ? items.item.cc : '' }}
        </template>
        <template v-slot:cell(plan)="items">
          <validation-provider
            v-if="detail.approval_status.isEdit || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required' : ''"
            v-slot="{ errors }"
            slim
          >
            <b-form-select
              v-model="items.item.plan"
              :options="budgetCodeArr[items.index].plan"
              size="sm"
              @change="onFieldChange('plan', items.item, items.index)"
              :class="{ 'is-invalid': errors.length }"
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  -
                </b-form-select-option>
              </template>
            </b-form-select>
          </validation-provider>
          {{ !detail.approval_status.isEdit ? items.item.plan : '' }}
        </template>
        <template v-slot:cell(ac)="items">
          <validation-provider
            v-if="detail.approval_status.isEdit || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required' : ''"
            v-slot="{ errors }"
            slim
          >
            <b-form-select
              v-if="detail.approval_status.isEdit"
              v-model="items.item.ac"
              :options="budgetCodeArr[items.index].ac"
              size="sm"
              @change="onFieldChange('ac', items.item, items.index)"
              :class="{ 'is-invalid': errors.length }"
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  -
                </b-form-select-option>
              </template>
            </b-form-select>
          </validation-provider>
          {{ !detail.approval_status.isEdit ? items.item.ac : '' }}
        </template>
        <template v-slot:cell(sub_ac)="items">
          <validation-provider
            v-if="detail.approval_status.isEdit || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required' : ''"
            v-slot="{ errors }"
            slim
          >
            <b-form-select
              v-if="detail.approval_status.isEdit"
              v-model="items.item.sub_ac"
              :options="budgetCodeArr[items.index].sub_ac"
              size="sm"
              @change="onFieldChange('sub_ac', items.item, items.index)"
              :class="{ 'is-invalid': errors.length }"
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  -
                </b-form-select-option>
              </template>
            </b-form-select>
          </validation-provider>
          {{ !detail.approval_status.isEdit ? items.item.sub_ac : '' }}
        </template>
        <template v-slot:cell(pa)="items">
          <validation-provider
            v-if="detail.approval_status.isEdit || (canAdd && detail.status === 'Draft')"
            :rules="isSubmit ? 'required' : ''"
            v-slot="{ errors }"
            slim
          >
            <b-form-select
              v-if="detail.approval_status.isEdit"
              v-model="items.item.pa"
              :options.sync="budgetCodeArr[items.index].pa"
              size="sm"
              :class="{ 'is-invalid': errors.length }"
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  -
                </b-form-select-option>
              </template>
            </b-form-select>
          </validation-provider>
          {{ !detail.approval_status.isEdit ? items.item.pa : '' }}
        </template>
        <template v-slot:cell(actions)="{ index }" v-if="canAdd && detail.approval_status.isEdit">
          <b-button
            variant="link"
            class="text-decoration-none text-danger p-0"
            @click="$emit('remove', index)"
          >
            <fa-icon icon="times-circle" />
          </b-button>
        </template>
        <template v-slot:custom-foot>
          <tr class="bg-color text-center">
            <td colspan="6" class="font-weight-bold">Sub Total</td>
            <td>{{ subTotal('volume_bcm') ? formatPrice(subTotal('volume_bcm')) : 0 }}</td>
            <td></td>
            <td class="text-right">{{ subTotal('total_price') ? formatPrice(subTotal('total_price')) : 0 }}</td>
            <td colspan="5"></td>
          </tr>
        </template>
      </b-table>
    </div>

    <div class="total border px-3 mt-4 ">
      <b-row class="bg-color">
        <b-col class="border-right border-bottom font-weight-bold p-2">GRAND TOTAL PAYABLE EXCLUDE VAT</b-col>
        <b-col class="text-right border-bottom font-weight-bold p-2">{{ formatPrice(detail.grant_total_payable) }}</b-col>
      </b-row>
      <b-row class="bg-color">
        <b-col class="border-right border-bottom font-weight-bold p-2">GRAND TOTAL PAYABLE INCLUDE VAT (11%)</b-col>
        <b-col class="text-right border-bottom font-weight-bold p-2">{{ formatPrice(detail.grant_total_payable_vat) }}</b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  props: {
    detail: {
      types: Object,
      default: () => ({
        volume: []
      })
    },
    canAdd: {
      types: Boolean,
      default: () => (false)
    },
    calculate: {
      types: Array,
      default: () => ([])
    },
    fuelPrice: {
      types: Number,
      default: () => (0)
    },
    isSubmit: {
      types: Boolean,
      default: () => (false)
    }
  },

  data: () => ({
    fields: [
      { key: 'actions', tdClass: 'text-center' },
      { key: 'no', label: 'No', tdClass: 'text-center' },
      { key: 'type_work_source', label: 'Type of Work (Source)', tdClass: 'text-center', thStyle: 'width: 150px' },
      { key: 'type_work_purpose', label: 'Type of Work (Purpose)', tdClass: 'text-center', thStyle: 'width: 150px' },
      { key: 'actual_distance', label: 'Actual Distance', tdClass: 'text-center' },
      { key: 'payment_distance', label: 'Payment Distance (M)', tdClass: 'text-right' },
      { key: 'volume_bcm', label: 'Volume (BCM)', tdClass: 'text-center' },
      { key: 'volume_price', label: 'Volume Price (IDR/BCM)', tdClass: 'text-center' },
      { key: 'total_price', label: 'Total Price (IDR)', tdClass: 'text-right' },
      { key: 'cc', label: 'CC', tdClass: 'text-center', thStyle: 'width: 120px' },
      { key: 'plan', label: 'Plan', tdClass: 'text-center', thStyle: 'width: 120px' },
      { key: 'ac', label: 'A/C', tdClass: 'text-center', thStyle: 'width: 120px' },
      { key: 'sub_ac', label: 'Sub A/C', tdClass: 'text-center', thStyle: 'width: 120px' },
      { key: 'pa', label: 'P/A', tdClass: 'text-center', thStyle: 'width: 120px' }
    ],
    ccList: null,
    budgetCodeArr: [],
    emptyBudgetCode: {
      plan: [],
      ac: [],
      sub_ac: [],
      pa: []
    },
    budgetAction: null
  }),

  watch: {
    'detail.volume'(value) {
      if (value) {
        this.fetchCC()
        for (var index = 0; index < value.length; index++) {
          if (!this.budgetCodeArr[index]) {
            this.budgetCodeArr.push({ ...this.emptyBudgetCode })
          }
          this.fetchPlan(value[index], index)
          this.fetchAC(value[index], index)
          this.fetchSubAC(value[index], index)
          this.fetchPA(value[index], index)
        }
      }
    }
  },

  methods: {
    async fetchCC() {
      this.ccList = await api.backupPayment.cc()
    },
    async fetchPlan(data, index) {
      this.budgetCodeArr[index].plan = await api.backupPayment.plan({
        cc: data.cc
      })
      if (this.budgetAction === 'change') {
        this.budgetCodeArr[index].ac = []
        this.budgetCodeArr[index].sub_ac = []
        this.budgetCodeArr[index].pa = []

        this.detail.volume[index].plan = ''
        this.detail.volume[index].ac = ''
        this.detail.volume[index].sub_ac = ''
        this.detail.volume[index].pa = ''
      }
    },
    async fetchAC(data, index) {
      this.budgetCodeArr[index].ac = await api.backupPayment.ac({
        cc: data.cc,
        plan: data.plan
      })
      if (this.budgetAction === 'change') {
        this.budgetCodeArr[index].sub_ac = []
        this.budgetCodeArr[index].pa = []

        this.detail.volume[index].ac = ''
        this.detail.volume[index].sub_ac = ''
        this.detail.volume[index].pa = ''
      }
    },
    async fetchSubAC(data, index) {
      this.budgetCodeArr[index].sub_ac = await api.backupPayment.subAC({
        cc: data.cc,
        plan: data.plan,
        ac: data.ac
      })
      if (this.budgetAction === 'change') {
        this.budgetCodeArr[index].pa = []

        this.detail.volume[index].sub_ac = ''
        this.detail.volume[index].pa = ''
      }
    },
    async fetchPA(data, index) {
      this.budgetCodeArr[index].pa = await api.backupPayment.pa({
        cc: data.cc,
        plan: data.plan,
        ac: data.ac,
        sub_ac: data.sub_ac
      })

      if (this.budgetAction === 'change') {
        this.detail.volume[index].pa = ''
      }
    },
    onFieldChange(value, data, index) {
      this.budgetAction = 'change'
      if (value === 'cc' && data.cc) {
        this.fetchPlan(data, index)
      } else if (value === 'plan' && data.plan) {
        this.fetchAC(data, index)
      } else if (value === 'ac' && data.ac) {
        this.fetchSubAC(data, index)
      } else if (value === 'sub_ac' && data.sub_ac) {
        this.fetchPA(data, index)
      }
    },
    subTotal(key) {
      let x = 0
      if (this.detail.volume) {
        this.detail.volume.forEach(d => {
          x += parseFloat(d[key])
        })
      }
      return x
    },
    formatPrice(value, decLen = 2) {
      return value !== undefined ? Number(value.toFixed(decLen)).toLocaleString(undefined, { minimumFractionDigits: decLen, maximumFractionDigits: decLen }) : ''
    },
    calVolumePrice(data) {
      if (this.calculate.length === 1) {
        data.volume_price = (this.calculate[0].base_rate_volume_bcm + (this.calculate[0].fuel_const * this.fuelPrice))
      } else if (this.calculate.length > 1) {
        data.volume_price = (
          (this.calculate[0].base_rate_volume_bcm + (this.calculate[0].fuel_const * this.fuelPrice)) +
          (this.calculate[1].base_rate_volume_bcm *
            ((data.payment_distance - 1000) / this.calculate[1].divisor) +
            ((this.calculate[1].fuel_const * this.fuelPrice) * ((data.payment_distance - 1000) / this.calculate[1].divisor))
          )
        )
      } else {
        data.volume_price = 0
      }
      return data.volume_price
    },
    calTotalPrice(data) {
      if (data.volume_price !== 0) {
        data.total_price = data.volume_bcm * data.volume_price
      } else {
        data.volume_price = 0
      }
      this.calGrandTotal()
      return data.total_price
    },
    calGrandTotal() {
      let totalPaymentAmount = 0
      let totalPrice = 0
      this.detail.equipment.forEach(d => {
        d.rowBUP.map(d => {
          totalPaymentAmount += d.payment_amount
        })
      })
      this.detail.volume.forEach(d => {
        totalPrice += d.total_price
      })
      this.detail.grant_total_payable = totalPaymentAmount + totalPrice
      this.detail.grant_total_payable_vat = this.detail.grant_total_payable * 1.11
    },
    addWorkVolume() {
      this.budgetAction = 'add'
      this.budgetCodeArr.push({ ...this.emptyBudgetCode })
      this.$emit('add')
    },
    getPaymentDistance(index, value) {
      if (index >= 0) {
        if (this.calculate.length && value) {
          this.detail.volume[index].payment_distance = value < this.calculate[0].volume_work_to ? this.calculate[0].volume_work_to : value
        } else {
          this.detail.volume[index].payment_distance = 0
        }
        return Number(this.detail.volume[index].payment_distance)
      }
    }
  }
}
</script>

<style>

</style>
